import { watch, type Ref } from "vue";
import type { GeoJSONSource, Map as MapboxMap, MapMouseEvent } from "mapbox-gl";
import type { Feature, FeatureCollection, Polygon, Position } from "geojson";
import { fitBoundsAdaptive, isValidPointPosition, withValidPointPositions, zoomAnimationDuration, zoomToClusterLeaves } from "../utils/mapZoom";
import { mapCountLabelLayout, mapCountTextField } from "../utils/mapLabel";
import {
  bindPointerCursor,
  EMPTY_FEATURE_COLLECTION,
  MAP_LABEL_COLOR,
  MAP_POINT_COLOR,
  POINT_HIT_STROKE,
  removeLayersIfPresent,
  removeSourcesIfPresent,
  useAppMapStyleLayers,
} from "./useAppMapStyleLayers";

const SOURCE_HEX = "sites-hexgrid";
const SOURCE_POINTS = "sites-hexgrid-points";
const LAYER_FILL = "sites-hexgrid-fill";
const LAYER_POINTS = "sites-hexgrid-circles";
const LAYER_CLUSTERS = "sites-hexgrid-cluster-circles";
const LAYER_CLUSTER_COUNT = "sites-hexgrid-cluster-count";

const TRANSITION_MIN_ZOOM = 11;
const TRANSITION_MAX_ZOOM = 12;

const CLUSTER_COUNT_SIZE = 13;

const POINT_CLUSTER_RADIUS = 30;
const POINT_CLUSTER_MAX_ZOOM = 16;
const POINT_UNCLUSTER_ZOOM = POINT_CLUSTER_MAX_ZOOM + 1;
const UNCLUSTERED_FILTER = ["!", ["has", "point_count"]] as const;

const SQRT3 = Math.sqrt(3);
const DEG_TO_RAD = Math.PI / 180;

interface ViewportBounds {
  west: number;
  east: number;
  south: number;
  north: number;
}

const VIEWPORT_PADDING = 1; // 100% buffer on each side

function getBufferedBounds(m: MapboxMap): ViewportBounds {
  const bounds = m.getBounds()!;
  const lngSpan = bounds.getEast() - bounds.getWest();
  const latSpan = bounds.getNorth() - bounds.getSouth();
  return {
    west: bounds.getWest() - lngSpan * VIEWPORT_PADDING,
    east: bounds.getEast() + lngSpan * VIEWPORT_PADDING,
    south: bounds.getSouth() - latSpan * VIEWPORT_PADDING,
    north: bounds.getNorth() + latSpan * VIEWPORT_PADDING,
  };
}

function normalizeLng(lng: number, refWest: number): number {
  while (lng < refWest) lng += 360;
  while (lng >= refWest + 360) lng -= 360;
  return lng;
}

function filterPoints(points: Feature[], bounds: ViewportBounds): Feature[] {
  return points.filter((pt) => {
    if (pt.geometry.type !== "Point") return false;
    if (!isValidPointPosition(pt.geometry.coordinates[0], pt.geometry.coordinates[1])) return false;
    const lng = normalizeLng(pt.geometry.coordinates[0]!, bounds.west);
    const lat = pt.geometry.coordinates[1]!;
    return lng >= bounds.west && lng <= bounds.east && lat >= bounds.south && lat <= bounds.north;
  });
}

interface UseAppMapHexgridOptions {
  data: Ref<FeatureCollection | undefined>;
  activeFeatureId?: Ref<number | string | undefined>;
  onFeatureClick?: (payload: { feature: GeoJSON.Feature; event: MapMouseEvent }) => void;
  localGlyphs?: Ref<boolean>;
}

/** Convert latitude (degrees) to Mercator Y (radians). */
export function latToMercatorY(lat: number): number {
  const rad = lat * DEG_TO_RAD;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
}

/** Convert Mercator Y (radians) back to latitude (degrees). */
export function mercatorYToLat(y: number): number {
  return (2 * Math.atan(Math.exp(y)) - Math.PI / 2) / DEG_TO_RAD;
}

const TARGET_HEX_WIDTH_PX = 18;
const HEX_GAP_PX = 1;
const HEX_INSET = (TARGET_HEX_WIDTH_PX - HEX_GAP_PX) / TARGET_HEX_WIDTH_PX;
const TILE_SIZE_PX = 512;
const ZOOM_STEP = 0.5;

export function snappedZoom(zoom: number): number {
  return Math.floor(zoom / ZOOM_STEP) * ZOOM_STEP;
}

export function computeCellSize(map: MapboxMap): number {
  const sz = snappedZoom(map.getZoom());
  const TWO_PI = 2 * Math.PI;
  const worldPx = TILE_SIZE_PX * Math.pow(2, sz);
  const n = Math.max(Math.round(worldPx / TARGET_HEX_WIDTH_PX), 1);
  return TWO_PI / (SQRT3 * n);
}

/**
 * Build a FeatureCollection of hex polygons from point data.
 * Binning and vertex generation are done in Mercator space (isotropic)
 * to avoid projection distortion, then converted back to geographic coordinates.
 */
export function buildHexFeatures(map: MapboxMap, points: Feature[]): FeatureCollection<Polygon> {
  const empty: FeatureCollection<Polygon> = { type: "FeatureCollection", features: [] };
  if (!points.length) return empty;

  // Fixed global origin so the grid tiles perfectly across world copies
  const originX = 0;
  const originY = 0;
  const cellSize = computeCellSize(map);

  // Bin points into hex cells using Mercator coordinates
  interface HexBin {
    count: number;
    west: number;
    south: number;
    east: number;
    north: number;
  }
  const bins = new Map<string, HexBin>();

  for (const pt of points) {
    if (pt.geometry.type !== "Point") continue;
    const lng = pt.geometry.coordinates[0]!;
    const lat = pt.geometry.coordinates[1]!;

    const mx = lng * DEG_TO_RAD;
    const my = latToMercatorY(lat);

    // Normalize to grid units
    const x = (mx - originX) / cellSize;
    const y = (my - originY) / cellSize;

    // Pixel to axial (pointy-top)
    const q = (SQRT3 / 3) * x - (1 / 3) * y;
    const r = (2 / 3) * y;

    // Cube round
    const s = -q - r;
    let rq = Math.round(q);
    let rr = Math.round(r);
    const rs = Math.round(s);

    const dq = Math.abs(rq - q);
    const dr = Math.abs(rr - r);
    const ds = Math.abs(rs - s);

    if (dq > dr && dq > ds) {
      rq = -rr - rs;
    } else if (dr > ds) {
      rr = -rq - rs;
    }

    const key = `${rq},${rr}`;
    const bin = bins.get(key);
    if (bin) {
      bin.count++;
      if (lng < bin.west) bin.west = lng;
      if (lng > bin.east) bin.east = lng;
      if (lat < bin.south) bin.south = lat;
      if (lat > bin.north) bin.north = lat;
    } else {
      bins.set(key, { count: 1, west: lng, south: lat, east: lng, north: lat });
    }
  }

  // Generate hex polygons for non-empty bins
  const features: Feature<Polygon>[] = [];
  let nextId = 0;

  for (const [key, bin] of bins) {
    const parts = key.split(",");
    const q = Number(parts[0]);
    const r = Number(parts[1]);

    // Center in Mercator space (pointy-top)
    const centerMercX = originX + cellSize * (SQRT3 * q + (SQRT3 / 2) * r);
    const centerMercY = originY + cellSize * ((3 / 2) * r);

    const renderRadius = cellSize * HEX_INSET;
    const ring: Position[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i + Math.PI / 6;
      const vMercX = centerMercX + renderRadius * Math.cos(angle);
      const vMercY = centerMercY + renderRadius * Math.sin(angle);
      ring.push([vMercX / DEG_TO_RAD, mercatorYToLat(vMercY)]);
    }
    ring.push([ring[0]![0]!, ring[0]![1]!]); // Close the ring

    features.push({
      type: "Feature",
      id: nextId++,
      properties: { count: bin.count, west: bin.west, south: bin.south, east: bin.east, north: bin.north },
      geometry: {
        type: "Polygon",
        coordinates: [ring],
      },
    });
  }

  return { type: "FeatureCollection", features };
}

export function useAppMapHexgrid(map: Ref<MapboxMap | undefined>, options: UseAppMapHexgridOptions) {
  let lastSnappedZoom: number | undefined;
  let lastBuildBounds: ViewportBounds | undefined;
  let previousProjection: string | undefined;

  // Clusters et comptage des hexagones opèrent au niveau des données source : exclure la feature active en amont est le seul moyen de l'en sortir tant que son POI reste ouvert.
  function featuresWithoutActive(features: Feature[]): Feature[] {
    const id = options.activeFeatureId?.value;
    if (id == null) return features;
    return features.filter((f) => String(f.properties?.id) !== String(id));
  }

  function pointsDataWithoutActive(): FeatureCollection {
    const data = withValidPointPositions(options.data.value ?? EMPTY_FEATURE_COLLECTION);
    return { ...data, features: featuresWithoutActive(data.features) };
  }

  function applyPointsData(m: MapboxMap) {
    const source = m.getSource(SOURCE_POINTS) as GeoJSONSource | undefined;
    if (source) source.setData(pointsDataWithoutActive());
  }

  function rebuildHexgrid(m: MapboxMap) {
    if (m.getZoom() >= TRANSITION_MAX_ZOOM) return;
    const data = options.data.value;
    const bounds = getBufferedBounds(m);
    const filtered = filterPoints(featuresWithoutActive(data?.features ?? []), bounds);
    lastBuildBounds = bounds;
    const fc = buildHexFeatures(m, filtered);
    const source = m.getSource(SOURCE_HEX) as GeoJSONSource | undefined;
    if (source) {
      source.setData(fc);
    }
  }

  function setup(m: MapboxMap) {
    // Force Mercator projection so hex grid matches the display
    previousProjection = m.getProjection()?.name ?? "globe";
    m.setProjection("mercator");

    const data = options.data.value;
    const rawData = data ?? EMPTY_FEATURE_COLLECTION;
    const bounds = getBufferedBounds(m);
    const filtered = filterPoints(featuresWithoutActive(rawData.features), bounds);
    lastBuildBounds = bounds;
    const fc = buildHexFeatures(m, filtered);

    m.addSource(SOURCE_HEX, {
      type: "geojson",
      data: fc,
    });

    m.addLayer({
      id: LAYER_FILL,
      type: "fill",
      source: SOURCE_HEX,
      maxzoom: TRANSITION_MAX_ZOOM,
      paint: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["zoom"],
          0,
          [
            "interpolate",
            ["linear"],
            ["case", ["boolean", ["feature-state", "hover"], false], 0.1, 0],
            0,
            ["step", ["get", "count"], "#F6E65B", 35, "#F6A15B", 200, "#F65B5B"],
            1,
            "white",
          ],
          TRANSITION_MIN_ZOOM,
          [
            "interpolate",
            ["linear"],
            ["case", ["boolean", ["feature-state", "hover"], false], 0.1, 0],
            0,
            ["step", ["get", "count"], "#F6E65B", 4, "#F6A15B", 20, "#F65B5B"],
            1,
            "white",
          ],
        ],
      },
    });

    m.addSource(SOURCE_POINTS, {
      type: "geojson",
      data: pointsDataWithoutActive(),
      cluster: true,
      clusterRadius: POINT_CLUSTER_RADIUS,
      clusterMaxZoom: POINT_CLUSTER_MAX_ZOOM,
    });

    m.addLayer({
      id: LAYER_POINTS,
      type: "circle",
      source: SOURCE_POINTS,
      minzoom: TRANSITION_MAX_ZOOM,
      filter: UNCLUSTERED_FILTER as never,
      paint: {
        "circle-radius": 12,
        "circle-color": MAP_POINT_COLOR,
        "circle-stroke-width": POINT_HIT_STROKE,
        "circle-stroke-opacity": 0,
      },
    });

    m.addLayer({
      id: LAYER_CLUSTERS,
      type: "circle",
      source: SOURCE_POINTS,
      minzoom: TRANSITION_MAX_ZOOM,
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": 16,
        "circle-color": MAP_POINT_COLOR,
        "circle-stroke-width": 0,
      },
    });

    m.addLayer({
      id: LAYER_CLUSTER_COUNT,
      type: "symbol",
      source: SOURCE_POINTS,
      minzoom: TRANSITION_MAX_ZOOM,
      filter: ["has", "point_count"],
      layout: mapCountLabelLayout({
        localGlyphs: options.localGlyphs?.value ?? false,
        textField: mapCountTextField(undefined),
        textSize: CLUSTER_COUNT_SIZE,
      }),
      paint: {
        "text-color": MAP_LABEL_COLOR,
      },
    });

    const onHexClick = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_FILL] })[0];
      if (!feature || feature.geometry.type !== "Polygon") return;

      const { count, west, south, east, north } = feature.properties as {
        count: number;
        west: number;
        south: number;
        east: number;
        north: number;
      };

      if (count === 1) {
        m.flyTo({ center: [west, south], zoom: TRANSITION_MAX_ZOOM, duration: zoomAnimationDuration(m, TRANSITION_MAX_ZOOM) });
      } else {
        fitBoundsAdaptive(
          m,
          [
            [west, south],
            [east, north],
          ],
          TRANSITION_MAX_ZOOM
        );
      }

      options.onFeatureClick?.({ feature: feature as GeoJSON.Feature, event: e });
    };

    const onPointClick = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_POINTS] })[0];
      if (!feature) return;
      options.onFeatureClick?.({ feature: feature as GeoJSON.Feature, event: e });
    };

    const onClusterClick = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_CLUSTERS] })[0];
      if (!feature) return;
      const source = m.getSource(SOURCE_POINTS) as GeoJSONSource;
      zoomToClusterLeaves(m, source, feature as GeoJSON.Feature, POINT_UNCLUSTER_ZOOM);
    };

    let hoveredHexId: number | string | undefined;
    const onHexMouseMove = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_FILL] })[0];
      if (!feature || feature.id == null) return;
      if (hoveredHexId === feature.id) return;
      if (hoveredHexId != null) {
        m.setFeatureState({ source: SOURCE_HEX, id: hoveredHexId }, { hover: false });
      }
      hoveredHexId = feature.id as number | string;
      m.setFeatureState({ source: SOURCE_HEX, id: hoveredHexId }, { hover: true });
    };
    const onHexMouseLeave = () => {
      if (hoveredHexId != null) {
        m.setFeatureState({ source: SOURCE_HEX, id: hoveredHexId }, { hover: false });
        hoveredHexId = undefined;
      }
    };

    const onZoom = () => {
      const sz = snappedZoom(m.getZoom());
      if (sz === lastSnappedZoom) return;
      lastSnappedZoom = sz;
      rebuildHexgrid(m);
    };

    const onMove = () => {
      if (!lastBuildBounds || m.getZoom() >= TRANSITION_MAX_ZOOM) return;
      const vp = m.getBounds()!;
      if (
        vp.getWest() < lastBuildBounds.west ||
        vp.getEast() > lastBuildBounds.east ||
        vp.getSouth() < lastBuildBounds.south ||
        vp.getNorth() > lastBuildBounds.north
      ) {
        rebuildHexgrid(m);
      }
    };

    const unbindCursor = bindPointerCursor(m, LAYER_FILL, LAYER_POINTS, LAYER_CLUSTERS);
    m.on("click", LAYER_FILL, onHexClick);
    m.on("mousemove", LAYER_FILL, onHexMouseMove);
    m.on("mouseleave", LAYER_FILL, onHexMouseLeave);
    m.on("click", LAYER_POINTS, onPointClick);
    m.on("click", LAYER_CLUSTERS, onClusterClick);
    m.on("zoom", onZoom);
    m.on("move", onMove);

    return () => {
      unbindCursor();
      m.off("click", LAYER_FILL, onHexClick);
      m.off("mousemove", LAYER_FILL, onHexMouseMove);
      m.off("mouseleave", LAYER_FILL, onHexMouseLeave);
      m.off("click", LAYER_POINTS, onPointClick);
      m.off("click", LAYER_CLUSTERS, onClusterClick);
      m.off("zoom", onZoom);
      m.off("move", onMove);
      removeLayersIfPresent(m, LAYER_CLUSTER_COUNT, LAYER_CLUSTERS, LAYER_POINTS, LAYER_FILL);
      removeSourcesIfPresent(m, SOURCE_POINTS, SOURCE_HEX);
      if (previousProjection) {
        m.setProjection(previousProjection);
      }
    };
  }

  useAppMapStyleLayers(map, setup);

  watch(options.data, () => {
    const m = map.value;
    if (!m) return;
    applyPointsData(m);
    if (m.getSource(SOURCE_HEX)) {
      rebuildHexgrid(m);
    }
  });

  if (options.activeFeatureId) {
    watch(options.activeFeatureId, () => {
      const m = map.value;
      if (!m) return;
      applyPointsData(m);
      if (m.getSource(SOURCE_HEX)) rebuildHexgrid(m);
    });
  }
}
